import * as THREE from 'three'
import {
  createColor,
  createVector3,
  type GSplatColorInput,
  type GSplatVector3Input,
  solveWaveTime,
  writeColorArray,
  writeVector3Array,
} from '@/plugins/gsplat/effects/effectUtils'
import {
  GSplatShaderEffect,
  type GSplatShaderEffectOptions,
} from '@/plugins/gsplat/effects/GSplatShaderEffect'

const shaderGLSL = /* glsl */ `
uniform float uTime;
uniform vec3 uCenter;
uniform float uSpeed;
uniform float uAcceleration;
uniform float uDelay;
uniform vec3 uDotTint;
uniform vec3 uWaveTint;
uniform float uOscillationIntensity;
uniform float uEndRadius;
uniform float uBandWidth;
uniform float uDotSize;
uniform float uMaxRoundSize;
uniform float uRevealBlendDistance;

float g_dist;
float g_dotWavePos;
float g_liftTime;
float g_liftWavePos;

void initShared(vec3 center) {
  g_dist = length(center - uCenter);
  g_dotWavePos = uSpeed * uTime + 0.5 * uAcceleration * uTime * uTime;
  g_liftTime = max(0.0, uTime - uDelay);
  g_liftWavePos = uSpeed * g_liftTime + 0.5 * uAcceleration * g_liftTime * g_liftTime;
}

float hash(vec3 p) {
  return fract(sin(dot(p, vec3(127.1, 311.7, 74.7))) * 43758.5453);
}

void modifySplatCenter(inout vec3 center) {
  initShared(center);

  if (g_dist > uEndRadius) return;

  bool wavesActive = g_liftTime <= 0.0 || g_dist > g_liftWavePos - 1.5 * uBandWidth;
  if (wavesActive) {
    float phase = hash(center) * 6.28318;
    center.z += sin(uTime * 3.0 + phase) * uOscillationIntensity * 0.25;
  }

  float distToLiftWave = abs(g_dist - g_liftWavePos);
  if (distToLiftWave < uBandWidth && g_liftTime > 0.0) {
    float normalizedDist = distToLiftWave / uBandWidth;
    float liftAmount = (1.0 - normalizedDist) * sin(normalizedDist * 3.14159);
    center.z += liftAmount * uOscillationIntensity * 0.9;
  }
}

void modifySplatCovariance(vec3 originalCenter, vec3 modifiedCenter, inout vec3 covA, inout vec3 covB) {
  if (g_dist > uEndRadius) {
    gsplatMakeRound(covA, covB, 0.0);
    return;
  }

  float origSize = gsplatExtractSize(covA, covB);
  float scaleFactor;
  bool isLiftWave = g_liftTime > 0.0 && g_liftWavePos > g_dist;

  if (isLiftWave) {
    scaleFactor = (g_liftWavePos >= g_dist + uRevealBlendDistance)
      ? 1.0
      : mix(0.1, 1.0, (g_liftWavePos - g_dist) / max(uRevealBlendDistance, 1e-5));
  } else if (g_dist > g_dotWavePos + uRevealBlendDistance) {
    gsplatMakeRound(covA, covB, 0.0);
    return;
  } else if (g_dist > g_dotWavePos - uRevealBlendDistance) {
    float distToWave = abs(g_dist - g_dotWavePos);
    float normalizedWave = distToWave / max(uRevealBlendDistance, 1e-5);
    scaleFactor = (normalizedWave < 0.5)
      ? mix(0.1, 0.2, 1.0 - normalizedWave * 2.0)
      : mix(0.0, 0.1, smoothstep(g_dotWavePos + uRevealBlendDistance, g_dotWavePos - uRevealBlendDistance, g_dist));
  } else {
    scaleFactor = 0.1;
  }

  if (scaleFactor >= 1.0) {
    return;
  }

  if (isLiftWave) {
    float t = (scaleFactor - 0.1) * 1.111111;
    float dotSize = min(uDotSize * scaleFactor, uMaxRoundSize);
    float finalSize = min(mix(dotSize, origSize, t), origSize);
    vec3 roundA = covA;
    vec3 roundB = covB;
    vec3 scaledA = covA;
    vec3 scaledB = covB;
    gsplatMakeRound(roundA, roundB, finalSize);
    gsplatApplyUniformScale(scaledA, scaledB, scaleFactor);
    covA = mix(roundA, scaledA, t);
    covB = mix(roundB, scaledB, t);
  } else {
    float targetSize = min(min(scaleFactor * uDotSize, origSize), uMaxRoundSize);
    gsplatMakeRound(covA, covB, targetSize);
  }
}

void modifySplatColor(vec3 center, inout vec4 color) {
  if (g_dist > uEndRadius) return;

  if (g_liftTime > 0.0 && g_dist >= g_liftWavePos - 1.5 * uBandWidth && g_dist <= g_liftWavePos + 0.5 * uBandWidth) {
    float distToLift = abs(g_dist - g_liftWavePos);
    float liftIntensity = smoothstep(1.5 * uBandWidth, 0.0, distToLift);
    color.rgb += uWaveTint * liftIntensity;
  } else if (g_dist <= g_dotWavePos && (g_liftTime <= 0.0 || g_dist > g_liftWavePos + 0.5 * uBandWidth)) {
    float distToDot = abs(g_dist - g_dotWavePos);
    float dotIntensity = smoothstep(uBandWidth, 0.0, distToDot);
    color.rgb += uDotTint * dotIntensity;
  }
}
`

const shaderWGSL = /* wgsl */ `
var<private> g_dist: f32;
var<private> g_dotWavePos: f32;
var<private> g_liftTime: f32;
var<private> g_liftWavePos: f32;

fn hash(value: vec3f) -> f32 {
  return fract(sin(dot(value, vec3f(127.1, 311.7, 74.7))) * 43758.5453);
}

fn initShared(center: vec3f) {
  g_dist = length(center - uniform.uCenter);
  g_dotWavePos = uniform.uSpeed * uniform.uTime +
    0.5 * uniform.uAcceleration * uniform.uTime * uniform.uTime;
  g_liftTime = max(0.0, uniform.uTime - uniform.uDelay);
  g_liftWavePos = uniform.uSpeed * g_liftTime +
    0.5 * uniform.uAcceleration * g_liftTime * g_liftTime;
}

fn modifySplatCenter(center: ptr<function, vec3f>) {
  initShared(*center);
  if (g_dist > uniform.uEndRadius) { return; }

  let wavesActive = g_liftTime <= 0.0 ||
    g_dist > g_liftWavePos - 1.5 * uniform.uBandWidth;
  if (wavesActive) {
    let phase = hash(*center) * 6.28318;
    (*center) = vec3f(
      (*center).xy,
      (*center).z + sin(uniform.uTime * 3.0 + phase) * uniform.uOscillationIntensity * 0.25
    );
  }

  let distToLiftWave = abs(g_dist - g_liftWavePos);
  if (distToLiftWave < uniform.uBandWidth && g_liftTime > 0.0) {
    let normalizedDist = distToLiftWave / uniform.uBandWidth;
    let liftAmount = (1.0 - normalizedDist) * sin(normalizedDist * 3.14159);
    (*center) = vec3f(
      (*center).xy,
      (*center).z + liftAmount * uniform.uOscillationIntensity * 0.9
    );
  }
}

fn modifySplatRotationScale(
  originalCenter: vec3f,
  modifiedCenter: vec3f,
  rotation: ptr<function, vec4f>,
  scale: ptr<function, vec3f>
) {
  if (g_dist > uniform.uEndRadius) {
    *scale = vec3f(0.0);
    return;
  }

  let originalSize = length(*scale);
  let isLiftWave = g_liftTime > 0.0 && g_liftWavePos > g_dist;
  var scaleFactor = 0.1;

  if (isLiftWave) {
    scaleFactor = select(
      mix(0.1, 1.0, (g_liftWavePos - g_dist) /
        max(uniform.uRevealBlendDistance, 0.00001)),
      1.0,
      g_liftWavePos >= g_dist + uniform.uRevealBlendDistance
    );
  } else if (g_dist > g_dotWavePos + uniform.uRevealBlendDistance) {
    *scale = vec3f(0.0);
    return;
  } else if (g_dist > g_dotWavePos - uniform.uRevealBlendDistance) {
    let distToWave = abs(g_dist - g_dotWavePos);
    let normalizedWave = distToWave / max(uniform.uRevealBlendDistance, 0.00001);
    scaleFactor = select(
      mix(0.0, 0.1, smoothstep(
        g_dotWavePos + uniform.uRevealBlendDistance,
        g_dotWavePos - uniform.uRevealBlendDistance,
        g_dist
      )),
      mix(0.1, 0.2, 1.0 - normalizedWave * 2.0),
      normalizedWave < 0.5
    );
  }

  if (scaleFactor >= 1.0) { return; }

  if (isLiftWave) {
    let t = (scaleFactor - 0.1) * 1.111111;
    let dotSize = min(uniform.uDotSize * scaleFactor, uniform.uMaxRoundSize);
    let finalSize = min(mix(dotSize, originalSize, t), originalSize);
    let roundScale = vec3f(finalSize);
    let scaledScale = (*scale) * scaleFactor;
    *scale = mix(roundScale, scaledScale, t);
  } else {
    let targetSize = min(
      min(scaleFactor * uniform.uDotSize, originalSize),
      uniform.uMaxRoundSize
    );
    *scale = vec3f(targetSize);
  }
}

fn modifySplatColor(center: vec3f, color: ptr<function, vec4f>) {
  if (g_dist > uniform.uEndRadius) { return; }

  if (g_liftTime > 0.0 &&
      g_dist >= g_liftWavePos - 1.5 * uniform.uBandWidth &&
      g_dist <= g_liftWavePos + 0.5 * uniform.uBandWidth) {
    let distToLift = abs(g_dist - g_liftWavePos);
    let liftIntensity = smoothstep(1.5 * uniform.uBandWidth, 0.0, distToLift);
    (*color) = vec4f((*color).rgb + uniform.uWaveTint * liftIntensity, (*color).a);
  } else if (g_dist <= g_dotWavePos &&
             (g_liftTime <= 0.0 || g_dist > g_liftWavePos + 0.5 * uniform.uBandWidth)) {
    let distToDot = abs(g_dist - g_dotWavePos);
    let dotIntensity = smoothstep(uniform.uBandWidth, 0.0, distToDot);
    (*color) = vec4f((*color).rgb + uniform.uDotTint * dotIntensity, (*color).a);
  }
}
`

const DEFAULT_MIN_PIXEL_SIZE = 0.5

export interface GSplatRevealRadialOptions extends GSplatShaderEffectOptions {
  center?: GSplatVector3Input
  speed?: number
  acceleration?: number
  delay?: number
  dotTint?: GSplatColorInput
  waveTint?: GSplatColorInput
  oscillationIntensity?: number
  endRadius?: number
  bandWidth?: number
  dotSize?: number
  maxRoundSize?: number
  revealBlendDistance?: number
  minPixelSize?: number
}

export class GSplatRevealRadial extends GSplatShaderEffect {
  center: THREE.Vector3
  speed: number
  acceleration: number
  delay: number
  dotTint: THREE.Color
  waveTint: THREE.Color
  oscillationIntensity: number
  endRadius: number
  bandWidth: number
  dotSize: number
  maxRoundSize: number
  revealBlendDistance: number
  minPixelSize: number

  private readonly _centerArray = [0, 0, 0]
  private readonly _dotTintArray = [0, 0, 0]
  private readonly _waveTintArray = [0, 0, 0]

  constructor(options: GSplatRevealRadialOptions = {}) {
    super(options)
    this.center = createVector3(options.center, new THREE.Vector3(0, 0, 0))
    this.speed = options.speed ?? 1
    this.acceleration = options.acceleration ?? 5
    this.delay = options.delay ?? 2
    this.dotTint = createColor(options.dotTint, new THREE.Color(0, 1, 1))
    this.waveTint = createColor(options.waveTint, new THREE.Color(5, 0, 0))
    this.oscillationIntensity = options.oscillationIntensity ?? 0.1
    this.endRadius = options.endRadius ?? 25
    this.bandWidth = options.bandWidth ?? 1
    this.dotSize = options.dotSize ?? 0.05
    this.maxRoundSize = options.maxRoundSize ?? this.dotSize
    this.revealBlendDistance = options.revealBlendDistance ?? 1
    this.minPixelSize = options.minPixelSize ?? DEFAULT_MIN_PIXEL_SIZE
    this.uniforms.minPixelSize = { value: this.minPixelSize }
  }

  getShaderGLSL(): string {
    return shaderGLSL
  }

  getShaderWGSL(): string {
    return shaderWGSL
  }

  getCompletionTime(): number {
    return this.delay + solveWaveTime(this.endRadius, this.speed, this.acceleration)
  }

  isEffectComplete(): boolean {
    return this.effectTime >= this.getCompletionTime()
  }

  protected override updateEffect(effectTime: number): void {
    if (this.isEffectComplete()) {
      this.enabled = false
      return
    }

    this.setUniform('uTime', effectTime)
    this.setUniform('uCenter', writeVector3Array(this.center, this._centerArray))
    this.setUniform('uSpeed', this.speed)
    this.setUniform('uAcceleration', this.acceleration)
    this.setUniform('uDelay', this.delay)
    this.setUniform('uDotTint', writeColorArray(this.dotTint, this._dotTintArray))
    this.setUniform('uWaveTint', writeColorArray(this.waveTint, this._waveTintArray))
    this.setUniform('uOscillationIntensity', this.oscillationIntensity)
    this.setUniform('uEndRadius', this.endRadius)
    this.setUniform('uBandWidth', this.bandWidth)
    this.setUniform('uDotSize', this.dotSize)
    this.setUniform('uMaxRoundSize', this.maxRoundSize)
    this.setUniform('uRevealBlendDistance', this.revealBlendDistance)
    this.setUniform('minPixelSize', this.minPixelSize)
  }
}
